// Per-room construction take-off math (floor area, tile area, wall length,
// baseboard length, wall area, paint area). Pure: returns plain numbers and
// data needed to render breakdowns / canvas overlays. No display formatting.

const { getObjectType } = require('./fixtureTypeUtils');
const { unionIntervals } = require('../../millwork/intervals');
const {
  wallProjectedSpan,
  wallLengthM,
  objAlongWall,
  objAlongExtent,
  openingsOnWall,
} = require('../../millwork/wallFrame');
const {
  getRoomFloorY,
  isFloorBlocker,
  isLShapeCornerCabinet,
  floorFootprintM2,
  isFloorLevelOpening,
  assignWallBlockers,
  blockerCutsFinish,
} = require('../../millwork/blockers');
const { millworkForRoom } = require('../../millwork/room');
const { regionRectOnWall, clampPct } = require('../../millwork/regions');
const { hydrateRoomOpenings } = require('./openingSyncUtils');
const { getWallAxisXZ } = require('../../geometry/wallAxis');
const { isPointInsidePolygon } = require('../../geometry/pointInPolygon');

// How far off a wall's middle the room-side probe steps.
const ROOM_SIDE_PROBE_M = 0.15;
const { footprintFor, lFootprint } = require('../../geometry/objectFootprint');

// ──── Wall texture regions ────────────────────────────────────────────
// `regionRectOnWall` / `clampPct` live in shared-lib/millwork/regions.js
// (baseboard reads the same region maths) and are re-exported below.

// A region "has an assigned finish" if it carries a real texture id or a
// paint color. Region drafts created by the UI (WallRegionSelector,
// onAddShowerFloor in StructureViewerWrapper) seed the id with a UUID
// like `region_1700123456_abc` or `showerfloor_1700123456_abc`; the
// save path in useWallTextureEditor.js overwrites that with the chosen
// texture id only if one was actually picked. When the draft is
// persisted without a texture pick, the UUID survives and the wall
// renders its base finish there — so the draft counts as whatever is
// underneath (usually paint), not as a material of its own.
//
// The id 'none' is the wall editor's old "No Pattern" choice, saved as if it
// were a finish (2026-10-20: billed as a material called "None"). The 3D
// view and the isolation views draw nothing for it, so it is no finish
// either; the editor no longer offers it.
const REGION_UUID_RE = /^[a-z]+_\d{10,}_[a-z0-9]+$/i;
const NO_PATTERN_ID = 'none';
function regionHasAssignedFinish(region) {
  if (region?.color) return true;
  const id = region?.id;
  if (!id) return false;
  if (id === NO_PATTERN_ID) return false;
  if (REGION_UUID_RE.test(id)) return false;
  return true;
}

// Painted region: `type: 'paint'`, a `paint_<HEX>` id (the persisted
// base-coat shape — it carries no `color` field), or an explicit color.
const isPaintRegion = (region) =>
  region?.type === 'paint' ||
  /^paint_/i.test(region?.id || '') ||
  !!region?.color;

// A wall region that renders a non-paint finish (shower wall tile, tub
// surround, backsplash…). Painted regions stay paint; unfinished drafts
// show the base finish and count as paint too.
const isWallTileRegion = (region) =>
  regionHasAssignedFinish(region) && !isPaintRegion(region);

// Sum of pairwise rect intersections (same {a0,a1,y0,y1} frame). Used to
// net openings back out of tile regions that span them.
const sumRectOverlap = (rectsA, rectsB) => {
  let total = 0;
  for (const r of rectsA) {
    for (const q of rectsB) {
      const w = Math.min(r.a1, q.a1) - Math.max(r.a0, q.a0);
      const h = Math.min(r.y1, q.y1) - Math.max(r.y0, q.y0);
      if (w > 0 && h > 0) total += w * h;
    }
  }
  return total;
};

/**
 * A wall's height for its regions' rectangles (`regionRectOnWall`): its own,
 * or its face's area over its length where it records none.
 */
const wallHeightM = (wall) => {
  const len = wallLengthM(wall);
  return wall?.dimensions?.y || (len > 0 ? wallAreaM2(wall) / len : 0);
};

/**
 * An opening's rectangle on its wall, `{ a0, a1, y0, y1 }` in metres — `a`
 * from the wall's left edge (`regionRectOnWall`'s frame), `y` up from the
 * room's floor (`floorY`) — or null for an opening with no size. An opening
 * with no height of its own sits at the floor when it is a floor-level kind.
 */
const openingRectOnWall = (opening, wall, floorY) => {
  const ow = opening?.dimensions?.x;
  const oh = opening?.dimensions?.y;
  if (typeof ow !== 'number' || typeof oh !== 'number') return null;
  const len = wallLengthM(wall);
  const a = objAlongWall(opening, wall);
  const cy = opening?.center?.y;
  const yMid =
    typeof cy === 'number'
      ? cy - floorY
      : isFloorLevelOpening(opening, floorY)
        ? oh / 2
        : 0;
  return {
    a0: a - ow / 2 + len / 2,
    a1: a + ow / 2 + len / 2,
    y0: Math.max(0, yMid - oh / 2),
    y1: Math.max(0, yMid + oh / 2),
  };
};

/**
 * Where a wall's own along-axis (`regionRectOnWall`'s `a`, 0 at the wall's
 * left edge and `wallLengthM` at its far one) sits on the floor plan: the plan
 * point where `a = 0` is, the unit direction `a` increases in, and the unit
 * normal pointing into the room — the side of the wall the room's floor
 * polygon lies on, read by probing a point off the middle of the wall
 * (`isPointInsidePolygon`). Null when the wall has no axis or the floor
 * polygon does not say which side is the room.
 */
const wallPlanFrame = (wall, floorPolygon) => {
  const { dirX, dirZ, midX, midZ } = getWallAxisXZ(wall);
  const len = wallLengthM(wall);
  if (!(len > 0) || !Array.isArray(floorPolygon) || floorPolygon.length < 3) {
    return null;
  }
  const span = wallProjectedSpan(wall);
  const uMid = midX * dirX + midZ * dirZ;
  const offset = span ? span.minU - uMid : -len / 2;
  const centerStart = { x: midX + dirX * offset, z: midZ + dirZ * offset };
  const probe = (sign) =>
    isPointInsidePolygon(
      {
        x: centerStart.x + dirX * (len / 2) - sign * dirZ * ROOM_SIDE_PROBE_M,
        z: centerStart.z + dirZ * (len / 2) + sign * dirX * ROOM_SIDE_PROBE_M,
      },
      floorPolygon
    );
  const plus = probe(1);
  if (plus === probe(-1)) return null;
  const sign = plus ? 1 : -1;
  const normal = { x: -sign * dirZ, z: sign * dirX };
  // the wall's centreline is half its thickness behind the room face
  const half = Math.max(0, wall?.dimensions?.z || 0) / 2;
  return {
    start: {
      x: centerStart.x + normal.x * half,
      z: centerStart.z + normal.z * half,
    },
    dir: { x: dirX, z: dirZ },
    normal,
  };
};

const wallAreaM2 = (wall) => {
  const corners = wall?.polygonCorners;
  if (Array.isArray(corners) && corners.length >= 3) {
    const { dirX, dirZ } = getWallAxisXZ(wall);
    const pts = corners.map((c) => {
      const cx = c?.x ?? (Array.isArray(c) ? c[0] : 0);
      const cy = c?.y ?? (Array.isArray(c) ? c[1] : 0);
      const cz = c?.z ?? (Array.isArray(c) ? c[2] : 0);
      return [cx * dirX + cz * dirZ, cy];
    });
    let a = 0;
    for (let i = 0; i < pts.length; i++) {
      const j = (i + 1) % pts.length;
      a += pts[i][0] * pts[j][1];
      a -= pts[j][0] * pts[i][1];
    }
    const area = Math.abs(a) / 2;
    if (area > 0) return area;
  }
  const dx = wall?.dimensions?.x;
  const dy = wall?.dimensions?.y;
  if (typeof dx === 'number' && typeof dy === 'number') return dx * dy;
  return 0;
};

// Build the footprint outline (corners in world X-Z) for an object.
// L-shape corner cabinets emit a 6-corner L (matching the drawing in
// objectDrawingUtils.js); everything else (incl. single-sided corner
// cabinets) emits the OBB rectangle.
const objFootprintCornersXZ = (obj) => {
  // The L's real 6-corner outline comes from the shared primitive. This used to
  // re-derive it here and applied R(+ry), the mirror of the handedness every
  // builder stores — up to 50.9" of vertex error at an oblique angle. The AABB
  // was unaffected, so areas and pricing were always right and only the drawn
  // shape was wrong.
  if (isLShapeCornerCabinet(obj)) {
    const l = lFootprint(obj);
    if (l) return l;
  }
  return footprintFor(
    { x: obj?.center?.x || 0, z: obj?.center?.z || 0 },
    obj?.dimensions?.x || 0,
    obj?.dimensions?.z || 0,
    obj?.rotation?.y || 0
  );
};

/**
 * Compute all take-off measurements + per-wall breakdown for a room.
 *
 * Returns:
 *   {
 *     floorAreaM2, tileAreaM2,
 *     totalLengthM, totalBaseboardM, totalBaseboardOffM,
 *     totalAreaM2, totalPaintM2, totalWallTileM2,
 *     floorPolygonCorners,           // [{x, z}, ...] or null
 *     tileBlockers: [{ type, corners: [{x,z},...] }, ...],
 *     millwork,                      // millworkForRoom(room): baseboard runs
 *                                    // per wall (null where it is off),
 *                                    // casing and crown
 *     wallStats: [
 *       {
 *         wall, index, name,
 *         length, baseboard, area, paint,
 *         openingCutLen, openingArea,   // openingCutLen: baseboard's
 *         baseboardOffLen, blockerBaseboardCut, blockerWallArea,
 *         openings, wallBlockers,
 *       },
 *       ...
 *     ],
 *   }
 */
/**
 * @param {Object} room - Room with walls, floors, doors, windows, openings, objects.
 * @param {Object} [structureOpenings] - Optional `{doors, windows, openings}`
 *   from the structure-level canonical arrays. When provided, room.doors /
 *   .windows / .openings are hydrated against this lookup so per-opening
 *   `dimensions` reads land on real width/height instead of undefined.
 *   Required for post-PR #634 (slim) data; legacy data ignores it gracefully.
 * @param {Object} [opts]
 * @param {(region) => string|null} [opts.materialTypeOf] a wall region's
 *   finish type (`tile`, `paint`, …); a region it cannot name does not stop
 *   baseboard
 */
function computeRoomTakeOffs(
  room,
  structureOpenings = null,
  { materialTypeOf = () => null } = {}
) {
  const effectiveRoom = structureOpenings
    ? hydrateRoomOpenings(room, structureOpenings)
    : room;
  const result = {
    floorAreaM2: 0,
    tileAreaM2: 0,
    totalLengthM: 0,
    totalBaseboardM: 0,
    // the same length by the style of the wall's run (`baseboardForWall`'s
    // `style`, always a known profile) — a wall with no run adds nothing
    baseboardByStyleM: {},
    // wall length with no baseboard because the wall has it switched off
    totalBaseboardOffM: 0,
    totalAreaM2: 0,
    totalPaintM2: 0,
    totalWallTileM2: 0,
    floorPolygonCorners: null,
    floorBBox: null,
    tileBlockers: [],
    floorTileCutRects: [],
    wallStats: [],
    // [{ kind, opening }] — the doors, windows and openings on this room's
    // walls, for the room plan; the openings are the hydrated ones
    planOpenings: [],
    // where the room's baseboard runs (`shared-lib/millwork`), the one rule
    // the estimate and the 3D view share
    millwork: null,
  };
  if (!room) return result;

  // Floor reference Y for "is this on the floor" checks. Pretransformed
  // RoomPlan data has floor at scan-coord Y like -1.31; transformed data
  // typically has floor at 0. Either way, derive it from the room.
  const floorY = getRoomFloorY(room);

  // Floor + tile
  if (room.floors && room.floors.length > 0 && room.floors[0].polygonCorners) {
    const corners = room.floors[0].polygonCorners;
    result.floorPolygonCorners = corners;
    let area = 0;
    let minX = Infinity;
    let maxX = -Infinity;
    let minZ = Infinity;
    let maxZ = -Infinity;
    for (let i = 0; i < corners.length; i++) {
      const j = (i + 1) % corners.length;
      area += corners[i].x * corners[j].z;
      area -= corners[j].x * corners[i].z;
      if (corners[i].x < minX) minX = corners[i].x;
      if (corners[i].x > maxX) maxX = corners[i].x;
      if (corners[i].z < minZ) minZ = corners[i].z;
      if (corners[i].z > maxZ) maxZ = corners[i].z;
    }
    result.floorAreaM2 = Math.abs(area) / 2;
    result.floorBBox = {
      minX,
      minZ,
      width: maxX - minX,
      depth: maxZ - minZ,
    };

    let blockedFootprintM2 = 0;
    for (const obj of room.objects || []) {
      if (!isFloorBlocker(obj, floorY)) continue;
      const ftp = floorFootprintM2(obj);
      if (ftp > 0) {
        blockedFootprintM2 += ftp;
        const ftpCorners = objFootprintCornersXZ(obj);
        result.tileBlockers.push({
          type: (getObjectType(obj) || '').toLowerCase(),
          corners: ftpCorners,
        });
        // AABB in floor-bbox-local coordinates ([0, width] × [0, depth]).
        // Used by the take-off aggregator for region-overlap math.
        // Skip showers: their footprint has a showerFloor region at the
        // same z, so a blocker rect here would zero its own region.
        const blockerType = (getObjectType(obj) || '').toLowerCase();
        if (blockerType !== 'shower') {
          let bx0 = Infinity;
          let bx1 = -Infinity;
          let bz0 = Infinity;
          let bz1 = -Infinity;
          for (const c of ftpCorners) {
            if (c.x < bx0) bx0 = c.x;
            if (c.x > bx1) bx1 = c.x;
            if (c.z < bz0) bz0 = c.z;
            if (c.z > bz1) bz1 = c.z;
          }
          result.floorTileCutRects.push({
            a0: bx0 - minX,
            a1: bx1 - minX,
            y0: bz0 - minZ,
            y1: bz1 - minZ,
          });
        }
      }
    }
    result.tileAreaM2 = Math.max(0, result.floorAreaM2 - blockedFootprintM2);
  }

  const millwork = millworkForRoom(room, {
    structureOpenings,
    materialTypeOf,
  });
  result.millwork = millwork;

  // Walls
  if (!room.walls || room.walls.length === 0) return result;

  const openingKind = new Map();
  const allOpenings = [
    ...(effectiveRoom.doors || []).map((o) => (openingKind.set(o, 'door'), o)),
    ...(effectiveRoom.windows || []).map(
      (o) => (openingKind.set(o, 'window'), o)
    ),
    ...(effectiveRoom.openings || []).map(
      (o) => (openingKind.set(o, 'opening'), o)
    ),
  ];
  const onWalls = new Set();

  // Wall-blocker assignment.
  //   - Cabinets / tubs / showers can touch multiple walls (alcove tub on
  //     3 walls, corner cabinet on 2). Each contributes to every wall it
  //     touches; per-wall cuts are unioned so corner overlaps don't
  //     double-count.
  //   - Appliances are assigned to ONLY their single closest qualifying
  //     wall (their back). They cut baseboard only on that wall, not on
  //     a side wall they happen to sit next to.
  const wallBlockerSet = assignWallBlockers(room.walls, room.objects, floorY);

  room.walls.forEach((wall, i) => {
    const len = wallLengthM(wall);
    const area = wallAreaM2(wall);
    const wallHeight = wallHeightM(wall);
    const openings = openingsOnWall(wall, allOpenings);
    for (const o of openings) onWalls.add(o);
    let openingArea = 0;
    // Vertical cuts (clipped to wall span) for paint-area math.
    const paintCuts = []; // [{a0, a1, y0, y1}]
    const wallBlockers = []; // breakdown display
    // Rects in [0, len] × [0, wallHeight] frame, for tile-region overlap
    // math in the take-off aggregator. Same data as paintCuts / openings
    // but shifted so a0=0 is the wall's left edge (not its centerline).
    const openingRects = [];
    const tileCutRects = [];

    for (const o of openings) {
      const rect = openingRectOnWall(o, wall, floorY);
      if (!rect) continue;
      openingArea += o.dimensions.x * o.dimensions.y;
      openingRects.push(rect);
    }

    for (const obj of wallBlockerSet.get(wall)) {
      const alongExt = objAlongExtent(obj, wall);
      const a = objAlongWall(obj, wall);
      // Clip to the wall's span so a blocker overhanging a short wall
      // doesn't subtract more than the wall actually has.
      const a0 = Math.max(-len / 2, a - alongExt / 2);
      const a1 = Math.min(len / 2, a + alongExt / 2);
      if (a1 <= a0) continue;
      const h = obj?.dimensions?.y || 0;
      // What cuts which finish is ONE rule, shared with the elevation
      // drawing (`blockerCutsFinish`): paint by cabinets / tubs / showers —
      // NOT appliances (the wall behind a fridge / stove / dishwasher stays
      // painted). We track the actual vertical span of the blocker so a
      // lower cabinet (0–0.85 m) and an upper cabinet (1.5–2.25 m) at the
      // same along-wall position both contribute their own bands.
      if (blockerCutsFinish(obj, 'paint')) {
        const cy = obj?.center?.y;
        let y0;
        let y1;
        if (typeof cy === 'number' && typeof h === 'number') {
          y0 = cy - h / 2 - floorY;
          y1 = cy + h / 2 - floorY;
        } else {
          // Missing position info — assume the blocker sits on the
          // floor with its given height.
          y0 = 0;
          y1 = h;
        }
        paintCuts.push({ a0, a1, y0, y1 });
        // Tile-region carve-out for the take-off aggregator — not a
        // shower: its footprint IS tiled (showerWall region covers the
        // same rect at the same z, so including the blocker would zero the
        // shower's own region). Tubs stay — the tub body occludes tile
        // below its top, with tubBacksplash above (disjoint vertically),
        // so the union behaves correctly.
        if (blockerCutsFinish(obj, 'tile')) {
          tileCutRects.push({
            a0: a0 + len / 2,
            a1: a1 + len / 2,
            y0,
            y1,
          });
        }
      }
      wallBlockers.push({ obj, along: a1 - a0, height: h });
    }

    // Baseboard comes from the millwork rule, and only where the wall has
    // it switched on (`wall.trim.hasBaseboard`).
    const board = millwork.baseboard[i];
    const baseboard = board
      ? board.spans.reduce((s, [a, b]) => s + (b - a), 0)
      : 0;
    // the run's profile, null where the wall has no run
    const baseboardStyle = board ? board.style : null;
    // the room panel's breakdown draws these in the centreline frame
    const baseboardSegments = board
      ? board.spans.map(([s, e]) => [s - len / 2, e - len / 2])
      : [];
    // The breakdown's three subtractions, which add up exactly:
    // length − baseboardOffLen − openingCutLen − blockerBaseboardCut =
    // baseboard. A wall with its baseboard off is all `baseboardOffLen`, so
    // its doors are not subtracted a second time.
    const baseboardOffLen = board ? 0 : len;
    const openingCutLen = board ? board.openingCutLenM : 0;
    const blockerBaseboardCut = board
      ? Math.max(0, len - baseboard - openingCutLen)
      : 0;

    // Paint-area cuts: union per-height-band is too costly for a panel
    // display, but flat-summing × height over-counts where two blockers
    // of similar height overlap on the wall. The simple fix: union the
    // along-wall ranges, then take the max blocker height that touches
    // each unioned segment. Acceptable for typical layouts.
    const blockerWallArea = computePaintAreaCut(paintCuts);

    // Wall tile regions (shower walls, tub surrounds, backsplashes) are
    // whatever finish the model actually renders there, so they leave
    // the paint total and get their own "wall tile" total. Openings that
    // sit inside a region (a window in a tub surround) are netted out of
    // the tile and must not be subtracted from paint a second time.
    const tileRegionRects = []; // [0, len] frame, for overlays
    const tileRegionCuts = []; // centerline frame, for the cut union
    for (const region of wall?.texture?.regions || []) {
      if (!isWallTileRegion(region)) continue;
      const r = regionRectOnWall(region, len, wallHeight);
      if (r.a1 <= r.a0 || r.y1 <= r.y0) continue;
      tileRegionRects.push(r);
      tileRegionCuts.push({
        a0: r.a0 - len / 2,
        a1: r.a1 - len / 2,
        y0: r.y0,
        y1: r.y1,
      });
    }
    const tileOpeningOverlap = sumRectOverlap(openingRects, tileRegionRects);
    const wallTile = Math.max(
      0,
      computePaintAreaCut(tileRegionCuts) - tileOpeningOverlap
    );
    const paintAreaCut = computePaintAreaCut([...paintCuts, ...tileRegionCuts]);
    const tileRegionPaintCut = Math.max(
      0,
      paintAreaCut - blockerWallArea - tileOpeningOverlap
    );

    const paint = Math.max(
      0,
      area - openingArea - blockerWallArea - tileRegionPaintCut
    );

    result.totalLengthM += len;
    result.totalBaseboardM += baseboard;
    if (baseboard > 0)
      result.baseboardByStyleM[baseboardStyle] =
        (result.baseboardByStyleM[baseboardStyle] ?? 0) + baseboard;
    result.totalBaseboardOffM += baseboardOffLen;
    result.totalAreaM2 += area;
    result.totalPaintM2 += paint;
    result.totalWallTileM2 += wallTile;
    result.wallStats.push({
      wall,
      index: i + 1,
      name: `Wall ${i + 1}`,
      length: len,
      height: wallHeight,
      baseboard,
      baseboardStyle,
      area,
      paint,
      wallTile,
      openingCutLen,
      openingArea,
      baseboardOffLen,
      blockerBaseboardCut,
      blockerWallArea,
      tileRegionPaintCut,
      openings,
      wallBlockers,
      baseboardSegments,
      openingRects,
      tileCutRects,
      tileRegionRects,
    });
  });

  result.planOpenings = allOpenings
    .filter((o) => onWalls.has(o))
    .map((opening) => ({ kind: openingKind.get(opening), opening }));
  return result;
}

// Compute the area covered by a union of (a0..a1, y0..y1) rectangles
// on the wall plane. Sweep along-wall: at each slice, union the vertical
// intervals from all rects spanning the slice, then accumulate
// width × union-height. Handles upper-over-lower cabinets correctly
// (their vertical bands are unioned, not max-merged) and corner
// overlap of same-height blockers (no double count).
function computePaintAreaCut(rects) {
  if (!rects.length) return 0;
  const xs = new Set();
  for (const { a0, a1 } of rects) {
    xs.add(a0);
    xs.add(a1);
  }
  const sorted = [...xs].sort((p, q) => p - q);
  let total = 0;
  for (let i = 0; i < sorted.length - 1; i++) {
    const x0 = sorted[i];
    const x1 = sorted[i + 1];
    if (x1 <= x0) continue;
    const yIntervals = [];
    for (const r of rects) {
      if (r.a0 <= x0 && r.a1 >= x1) yIntervals.push([r.y0, r.y1]);
    }
    const yUnion = unionIntervals(yIntervals);
    let yLen = 0;
    for (const [ys, ye] of yUnion) yLen += ye - ys;
    total += (x1 - x0) * yLen;
  }
  return total;
}

module.exports = {
  regionRectOnWall,
  openingRectOnWall,
  sumRectOverlap,
  wallHeightM,
  clampPct,
  REGION_UUID_RE,
  regionHasAssignedFinish,
  isPaintRegion,
  isWallTileRegion,
  computeRoomTakeOffs,
  computePaintAreaCut,
  wallPlanFrame,
};
